/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IAddressList,
    IAddressListItem,
    IAddressListProps,
    IAddressShowItem,
    IAddressShowProps,
    IAddressAddItem,
    IAddressAddUpdateProps
} from '@msdyn365-commerce-modules/address';
import { IAccountManagementAddressViewProps } from './dobbies-loyalty-account-management-address';

const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items &&
                items.map((item: IAddressShowItem) => {
                    const { key, description } = item;
                    if (key.toLowerCase() !== 'threeletterisoregionname') {
                        return <>{description}</>;
                    }
                    return;
                })}
        </Node>
    );
};

const AddressList: React.FC<IAddressList> = ({ List, isShowList, heading, items }) => {
    if (!isShowList) {
        return null;
    }

    return (
        <Node {...List}>
            {heading}
            {items &&
                items.map((item: IAddressListItem) => {
                    const { Item, key, error, showItems, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                    const { Error, isShowError, title, message } = error;
                    return (
                        <Node {...Item} key={key}>
                            <AddressShow {...showItems} />
                            {isShowError && (
                                <Node {...Error}>
                                    {title}
                                    {message}
                                </Node>
                            )}
                            {isShowPrimaryButton && primaryButton}
                            {editButton}
                            {removeButton}
                        </Node>
                    );
                })}
        </Node>
    );
};

const AddressLists: React.FC<IAddressListProps> = ({
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList
}) => {
    return (
        <Node {...ListAddress}>
            {heading}
            {isShowEmptyListMessage && emptyListMessage}
            {addButton}
            <AddressList {...primaryAddressList} />
            <AddressList {...otherAddressList} />
        </Node>
    );
};

const AddressAddUpdate: React.FC<IAddressAddUpdateProps & { postCodeInput: React.ReactNode }> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    postCodeInput
}) => {
    return (
        <Node {...AddressForm}>
            {heading}
            {/* VSI-Customization -- START Render input field for postcode and button  */}
            {postCodeInput}
            {/* VSI-Customization -- END  */}
            {/* Customization to hide country from form */}
            {items &&
                items.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, alert, input } = item;
                    if (key.toLowerCase() !== 'threeletterisoregionname') {
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {alert}
                                {input}
                            </Node>
                        );
                    } else {
                        return;
                    }
                })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const AccountManagementAddressView: React.FC<IAccountManagementAddressViewProps> = props => {
    const { AccountAddressManagement, viewState, showAddressList, showAddOrUpdateAddress } = props;

    return (
        <Module {...AccountAddressManagement}>
            {viewState.isShowAddresList && <AddressLists {...showAddressList} />}
            {viewState.isShowAddOrUpdateAddress && (
                <AddressAddUpdate {...showAddOrUpdateAddress} postCodeInput={props.renderPostCodeSearchInput} />
            )}
        </Module>
    );
};

export default AccountManagementAddressView;
